// components/admin/AmenitiesEditor.tsx
/**
 * Amenities editor with preset suggestions and custom input
 */
import { useState } from 'react';
import { Plus, X, Check } from 'lucide-react';

// Common building amenities - these show as quick-add buttons
const COMMON_AMENITIES = [
  'In-suite Laundry',
  'Gym',
  'Rooftop Terrace',
  'Concierge',
  'Underground Parking',
  'Bike Storage',
  'Party Room',
  'Pet Friendly',
  'Swimming Pool',
  'Sauna',
  'BBQ Area',
  'Package Lockers',
  'EV Charging',
  '24/7 Security',
  'Visitor Parking',
  'Storage Lockers',
  'High-Speed Internet',
  'Central Air',
  'Balcony/Patio',
  'Hardwood Floors',
];

interface AmenitiesEditorProps {
  amenities: string[];
  onChange: (amenities: string[]) => void;
}

export function AmenitiesEditor({ amenities, onChange }: AmenitiesEditorProps) {
  const [customInput, setCustomInput] = useState('');

  const addAmenity = (amenity: string) => {
    if (amenity.trim() && !amenities.includes(amenity.trim())) {
      onChange([...amenities, amenity.trim()]);
    }
  };

  const removeAmenity = (amenity: string) => {
    onChange(amenities.filter((a) => a !== amenity));
  };

  const toggleAmenity = (amenity: string) => {
    if (amenities.includes(amenity)) {
      removeAmenity(amenity);
    } else {
      addAmenity(amenity);
    }
  };

  const handleCustomSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (customInput.trim()) {
      addAmenity(customInput);
      setCustomInput('');
    }
  };

  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium text-gray-700">
        Amenities & Features
      </label>

      {/* Selected amenities */}
      {amenities.length > 0 && (
        <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-lg">
          {amenities.map((amenity) => (
            <span
              key={amenity}
              className="inline-flex items-center gap-1 px-2.5 py-1 bg-white border border-gray-200 rounded-full text-sm text-gray-700"
            >
              {amenity}
              <button
                type="button"
                onClick={() => removeAmenity(amenity)}
                className="ml-0.5 text-gray-400 hover:text-red-500"
              >
                <X size={14} />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Quick-add presets */}
      <div className="space-y-2">
        <p className="text-xs text-gray-500">Quick add:</p>
        <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
          {COMMON_AMENITIES.map((amenity) => {
            const isSelected = amenities.includes(amenity);
            return (
              <button
                key={amenity}
                type="button"
                onClick={() => toggleAmenity(amenity)}
                className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs transition-colors ${
                  isSelected
                    ? 'bg-primary text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {isSelected && <Check size={12} />}
                {amenity}
              </button>
            );
          })}
        </div>
      </div>

      {/* Custom amenity input */}
      <form onSubmit={handleCustomSubmit} className="flex gap-2">
        <input
          type="text"
          value={customInput}
          onChange={(e) => setCustomInput(e.target.value)}
          placeholder="Add custom amenity..."
          className="flex-1 px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
        />
        <button
          type="submit"
          disabled={!customInput.trim()}
          className="px-3 py-1.5 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Plus size={16} />
        </button>
      </form>
    </div>
  );
}
